import React, { useMemo } from "react";
import {
  RiPhoneLine,
  RiMapPinLine,
  RiWomenLine,
  RiMenLine,
} from "@remixicon/react";
import { CardContent } from "@/components/ui/card";
import DashboardCardWrapper from "@/components/common/DashboardCardWrapper";
import DashboardCardHeader from "@/components/common/DashboardCardHeader";
import { Typography } from "@/components/ui/typography";
import DemographicsIcon from "@/assets/icons/demographics.svg";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import {
  calculateAgeFromDob,
  getInitialsFromName,
  formatDateWithoutTimezone,
  formatPhoneNumber,
} from "@/constants/utilityFunctions";
import { routes } from "@/utils/urls";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";
import DashboardFieldIcon from "./common/DashboardFieldIcon";

interface DemographicsData {
  medicaidId: string;
  memberFirstName: string;
  memberLastName: string;
  memberDob: string;
  agencyName: string;
  memberGender: string;
  networkName: string;
  memberRace: string;
  memberPhone: string;
  memberAddress1: string;
  memberAddress2: string;
  memberCity: string;
  memberState: string;
}

// Handle null values - replace with "-"
const handleNullValues = (
  data: DemographicsData | null
): DemographicsData | null => {
  if (!data || typeof data !== "object") return data;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processed: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") {
      processed[key] = "-";
    } else {
      processed[key] = value;
    }
  }
  return processed as DemographicsData;
};

const fieldIconMap: Record<
  string,
  React.ComponentType<{ size?: string | number; className?: string }> | string
> = {
  "Medicaid ID": "ri-health-book-line",
  "Medical Home": "ri-home-heart-line",
  Network: "ri-map-2-line",
  "DOB | Age": "ri-cake-2-line",
  "Sex at Birth": "ri-user-line",
  Race: "ri-group-2-line",
  "Phone Number": RiPhoneLine,
  Address: RiMapPinLine,
  City: "ri-community-line",
  State: "ri-government-line",
};

interface DemographicsCardProps {
  shouldBlink?: boolean;
}

const DemographicsCard: React.FC<DemographicsCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("DEMOGRAPHICS");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: demographics,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<DemographicsData>({
    endpoint: routes.cards.demographics,
    memberId,
    processData: (rawData: unknown) =>
      handleNullValues((rawData as { data: DemographicsData }).data),
  });

  const display = useMemo(() => {
    if (!demographics) return null;

    const fullName =
      `${demographics.memberFirstName} ${demographics.memberLastName}`.trim();
    const addressParts = [
      demographics.memberAddress1,
      demographics.memberAddress2,
    ].filter((part) => part && part !== "-");
    const fullAddress = addressParts.length > 0 ? addressParts.join(", ") : "-";

    const age =
      demographics.memberDob !== "-"
        ? calculateAgeFromDob(demographics.memberDob)
        : "-";
    const dobFormatted = formatDateWithoutTimezone(demographics.memberDob);

    const genderUpper = (demographics.memberGender || "").toUpperCase();
    let sexAtBirth = "Not Provided";
    if (demographics.memberGender === "-") {
      sexAtBirth = "-";
    } else if (genderUpper === "M" || genderUpper === "MALE") {
      sexAtBirth = "Male";
    } else if (genderUpper === "F" || genderUpper === "FEMALE") {
      sexAtBirth = "Female";
    }

    return {
      memberName: fullName !== " " ? fullName : "-",
      fields: {
        "Medicaid ID": demographics.medicaidId,
        "Medical Home": demographics.agencyName,
        Network: demographics.networkName,
        "DOB | Age": age !== "-" ? `${dobFormatted} | ${age} years` : "-",
        "Sex at Birth": sexAtBirth,
        Race: demographics.memberRace,
        "Phone Number": formatPhoneNumber(demographics.memberPhone),
        Address: fullAddress,
        City: demographics.memberCity,
        State: demographics.memberState,
      } as Record<string, string>,
    };
  }, [demographics]);

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <img
                src={DemographicsIcon}
                alt='Demographics'
                className='w-3 h-4'
              />
            </BorderedIconWrapper>
          }
          title='Demographics'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 h-max-content lg:h-[590px] gap-[12px]'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={16} showProfile={true} />;
            }

            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[523px] md:h-[523px]'>
                  <Typography
                    variant='medium-14'
                    className={`${isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                      } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }

            if (!demographics) {
              return null;
            }

            return (
              <>
              {/* User Section */}
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center border-[1px] border-grey-300 dark:border-[1px]  dark:border-grey-300'>
                  <Typography variant='semibold-16' className='text-orange-600'>
                    {display?.memberName && display.memberName !== "-"
                      ? getInitialsFromName(display.memberName)
                      : "-"}
                  </Typography>
                </div>
                <TruncatedTextTooltip text={display?.memberName || "-"}>
                  <Typography
                    variant='medium-16'
                    className='text-grey-800 dark:text-white truncate'>
                    {display?.memberName || "-"}
                  </Typography>
                </TruncatedTextTooltip>
              </div>

              {/* Data Fields */}
              <div className='gap-[16px] flex flex-col'>
                {display &&
                  Object.entries(display.fields).map(([key, value]) => {
                    const IconComponent = fieldIconMap[key];
                    return (
                      <div key={key} className='flex items-start gap-3'>
                        {/* Icon */}
                        <DashboardFieldIcon icon={IconComponent} />

                        {/* Field Key + Value */}
                        <div className='flex-1 flex justify-between items-start min-w-0'>
                          <Typography
                            variant='medium-14'
                            className='text-grey-700 dark:text-gray-400 flex-shrink-0 leading-[1.6]'>
                            {key}
                          </Typography>

                          <div className='flex items-center gap-2 min-w-0 max-w-[60%]'>
                            {key === "Sex at Birth" && value === "Female" && (
                              <RiWomenLine
                                size={20}
                                className='flex-shrink-0'
                                style={{ color: "#007A80" }}
                              />
                            )}
                            {key === "Sex at Birth" && value === "Male" && (
                              <RiMenLine
                                size={20}
                                className='flex-shrink-0'
                                style={{ color: "#007A80" }}
                              />
                            )}
                            {/* //unknown - */}
                            <TruncatedTextTooltip
                              text={value}
                              className='min-w-0 flex-1'>
                              <Typography
                                variant='medium-14'
                                className='
                                text-gray-800 dark:text-white text-right truncate
                                max-md:whitespace-normal max-md:break-words max-md:text-right max-md:block
                                leading-[1.6]
                              '>
                                {value}
                              </Typography>
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              </>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default DemographicsCard;
